import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import client, { getPin } from "@/lib/api";
import { downloadAuthedFile } from "@/lib/download";
import { TILL_LABEL, TIP_FLOAT_BOX } from "@/lib/constants";
import { formatDate, money } from "@/lib/format";
import { Download, AlertCircle, Coins, Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";

export default function TipLog() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total_tips: 0, paid_tips: 0, unpaid_tips: 0, by_person: {}, by_person_unpaid: {} });
  const [loading, setLoading] = useState(true);
  const [till, setTill] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [who, setWho] = useState("");
  const [showPaid, setShowPaid] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/tips", {
        params: {
          till: till || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          who: who || undefined,
        },
      });
      setData(data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) setForbidden(true);
      else toast.error("Couldn't load tip log");
    } finally {
      setLoading(false);
    }
  }, [till, dateFrom, dateTo, who]);

  useEffect(() => { load(); }, [load]);

  const togglePaid = async (id, paid) => {
    // optimistic
    setData((d) => ({
      ...d,
      items: d.items.map((it) => (it.id === id ? { ...it, tips_paid_out: paid } : it)),
    }));
    try {
      await client.patch(`/closes/${id}/tips-paid`, { tips_paid_out: paid });
      load();
    } catch {
      toast.error("Couldn't update — try again");
      load();
    }
  };

  const saveName = async (id, name) => {
    try {
      await client.patch(`/closes/${id}/who-worked`, { who_worked: name });
      toast.success("Name updated");
      load();
    } catch {
      toast.error("Couldn't save name");
    }
  };

  const downloadCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/tips/export.csv?pin=${encodeURIComponent(getPin())}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "till_trax_tips.csv";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch { /* node already detached */ } }, 1000);
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header onBack={() => nav(-1)} />
        <main className="max-w-md mx-auto px-5 py-10" data-testid="tips-forbidden">
          <div className="bg-white border border-[#E8E4DA] rounded-2xl p-6 text-center">
            <AlertCircle className="mx-auto text-[#D97706] mb-3" />
            <h2 className="text-xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Owner PIN required</h2>
            <p className="text-[#5C7566] text-sm mt-1">Sign in with the owner PIN to view the tip log.</p>
          </div>
        </main>
      </div>
    );
  }

  const unpaidPeople = Object.entries(data.by_person_unpaid || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const items = showPaid ? data.items : data.items.filter((i) => !i.tips_paid_out);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Tip log" onBack={() => nav("/dashboard")} />
      <main className="max-w-md mx-auto px-5 py-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Card tips pooled</h2>
            <p className="text-xs text-[#5C7566]">Both tills share one tip float box (${TIP_FLOAT_BOX}).</p>
          </div>
          <button
            data-testid="export-tips-csv"
            onClick={downloadCsv}
            className="h-10 px-3 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center gap-2 active:scale-95"
          >
            <Download size={14} /> CSV
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0D311E] text-[#FDFBF7] rounded-2xl p-4" data-testid="tips-unpaid-card">
            <div className="flex items-center gap-2 text-[#FDFBF7]/70 text-[10px] tracking-[0.22em] uppercase">
              <Coins size={12} /> Owed to staff
            </div>
            <div className="text-3xl tabular-nums mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
              {money(data.unpaid_tips)}
            </div>
            <div className="text-[11px] text-[#FDFBF7]/70 mt-1">to pay from the tip float box</div>
          </div>
          <div className="bg-[#F5F2EA] border border-[#E8E4DA] rounded-2xl p-4" data-testid="tips-paid-card">
            <div className="flex items-center gap-2 text-[#5C7566] text-[10px] tracking-[0.22em] uppercase">
              <Check size={12} /> Already paid
            </div>
            <div className="text-3xl tabular-nums mt-1 text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
              {money(data.paid_tips)}
            </div>
            <div className="text-[11px] text-[#5C7566] mt-1">of {money(data.total_tips)} total in filter</div>
          </div>
        </div>

        {unpaidPeople.length > 0 && (
          <div className="bg-white border border-[#E8E4DA] rounded-2xl p-4" data-testid="tips-by-person">
            <div className="text-[#5C7566] text-xs tracking-[0.18em] uppercase mb-3">Owed by person</div>
            <div className="space-y-2">
              {unpaidPeople.map(([name, amt]) => (
                <div key={name} className="flex justify-between items-baseline">
                  <span className="text-[#0D311E]">{name}</span>
                  <span className="tabular-nums text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{money(amt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E8E4DA] rounded-2xl p-4 space-y-3">
          <div className="text-[#5C7566] text-xs tracking-[0.18em] uppercase">Filters</div>
          <div className="grid grid-cols-2 gap-2">
            <select
              data-testid="tip-filter-till"
              value={till}
              onChange={(e) => setTill(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            >
              <option value="">Both tills</option>
              <option value="Pro Shop">Pro Shop</option>
              <option value="Grill">Grill</option>
            </select>
            <input
              data-testid="tip-filter-who"
              type="text"
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="Name (optional)"
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
              autoComplete="off"
            />
            <input
              data-testid="tip-filter-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            />
            <input
              data-testid="tip-filter-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2B533E]">
            <input
              data-testid="show-paid"
              type="checkbox"
              checked={showPaid}
              onChange={(e) => setShowPaid(e.target.checked)}
              className="h-4 w-4 accent-[#1A5D3A]"
            />
            Show already-paid tips
          </label>
        </div>

        {loading ? (
          <div className="text-center text-[#5C7566] py-10">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="tips-empty" className="text-center text-[#5C7566] py-10 bg-white border border-[#E8E4DA] rounded-2xl">
            {data.items.length === 0 ? "No tips logged in this range." : "Nothing left to pay out."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="tips-list">
            {items.map((t) => (
              <TipRow
                key={t.id}
                t={t}
                onToggle={togglePaid}
                onSaveName={saveName}
                onOpen={() => nav(`/close-detail/${t.id}`)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function TipRow({ t, onToggle, onSaveName, onOpen }) {
  const paid = !!t.tips_paid_out;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(t.who_worked || "");

  React.useEffect(() => { setDraft(t.who_worked || ""); }, [t.who_worked]);

  if (editing) {
    return (
      <div
        data-testid={`tip-row-${t.id}`}
        className="bg-white border border-[#1A5D3A] rounded-2xl p-4 space-y-3"
      >
        <div className="text-xs text-[#5C7566]">{formatDate(t.created_at)} · {TILL_LABEL[t.till] || t.till} · {money(t.tips)}</div>
        <input
          data-testid={`edit-name-input-${t.id}`}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          placeholder="Names, separated by commas"
          className="w-full bg-transparent outline-none text-lg text-[#0D311E] border-b-2 border-[#E8E4DA] focus:border-[#1A5D3A] py-1.5"
          style={{ fontFamily: "'Fraunces', serif" }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <div className="flex gap-2 pt-1">
          <button
            data-testid={`edit-name-save-${t.id}`}
            onClick={async () => { await onSaveName(t.id, draft); setEditing(false); }}
            className="flex-1 h-10 rounded-xl bg-[#1A5D3A] text-white text-sm active:scale-95"
          >
            Save
          </button>
          <button
            data-testid={`edit-name-cancel-${t.id}`}
            onClick={() => { setDraft(t.who_worked || ""); setEditing(false); }}
            className="h-10 px-4 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center gap-1.5"
          >
            <X size={14} /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`tip-row-${t.id}`}
      className={`bg-white border rounded-2xl p-4 flex items-center justify-between gap-3 ${paid ? "border-[#E8E4DA] opacity-70" : "border-[#E8E4DA]"}`}
    >
      <button onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className={`font-medium truncate ${paid ? "text-[#5C7566] line-through" : "text-[#0D311E]"}`}>
          {(t.who_worked || "—")}
        </div>
        <div className="text-xs text-[#5C7566] truncate">{formatDate(t.created_at)} · {TILL_LABEL[t.till] || t.till}</div>
      </button>
      <div className="shrink-0 flex items-center gap-2">
        <button
          data-testid={`edit-name-${t.id}`}
          aria-label="Edit names"
          onClick={() => setEditing(true)}
          className="h-9 w-9 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] flex items-center justify-center active:scale-95"
        >
          <Pencil size={14} />
        </button>
        <span
          className={`tabular-nums text-lg ${paid ? "text-[#5C7566]" : "text-[#0D311E]"}`}
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          {money(t.tips)}
        </span>
        <button
          data-testid={`mark-paid-${t.id}`}
          onClick={() => onToggle(t.id, !paid)}
          className={`h-9 px-3 rounded-xl text-xs font-medium transition-colors ${paid ? "bg-[#F5F2EA] text-[#5C7566] border border-[#E8E4DA]" : "bg-[#1A5D3A] text-white"}`}
        >
          {paid ? "Paid ✓" : "Mark paid"}
        </button>
      </div>
    </div>
  );
}
